import { Activity, Sparkles, Brain, Heart, Fingerprint, Zap, Layers, Info, UserCheck, ShieldCheck } from "lucide-react";
import { formatText } from "../../DanvinciHelpers";

export const Psychology = ({neuroNarrative, memoryMetrics, authenticity, access}) => {

    // --- DATA PREPARATION ---
    const neural = access('predicted_neural_arousal') || {};
    
    // 1. Derive Emotional Spectrum
    const derivedValence = (neural.vmPFC_predicted_arousal || 0) - (neural.insula_ACC_predicted_arousal || 0);
    const valence = access('avg_valence') ?? derivedValence;
    
    const derivedArousal = neural.amygdala_predicted_arousal || 0;
    const arousal = access('avg_arousal') ?? derivedArousal;

    // 2. New Data Points
    const striatum = neural.striatum_reward_prediction || 0; 
    const viralFactors = access('viral_potential_analysis.viral_factors_present') || [];
    
    // 3. BRAND PERSONALITY
    const brandTraits = access('visual_rhetoric_analysis.visualBrandAnalysis.brand_personality_profile') || [];
    const psychVibe = access('visual_rhetoric_analysis.visualBrandAnalysis.psychological_vibe') || "Neutral";

    // --- TOOLTIP COMPONENT ---
    const Tooltip = ({ text }) => (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-48 p-3 bg-slate-950/95 border border-slate-700 text-slate-300 text-[10px] leading-relaxed rounded-lg shadow-xl opacity-0 transition-opacity duration-0 delay-0 group-hover:opacity-100 group-hover:duration-300 group-hover:delay-1000 pointer-events-none z-[100]">
            {text}
            <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-950 border-r border-b border-slate-700 rotate-45"></div>
        </div>
    );

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300 pb-10">
            
            {/* 1. NEURAL ARCHITECTURE: The Core Brain Response */}
            {access('predicted_neural_arousal') && (
                <div className="bg-slate-800/50 p-5 rounded-xl border border-slate-700/50 relative">
                    
                    {/* Header */}
                    <h4 className="text-xs font-bold text-indigo-300 uppercase tracking-widest mb-6 flex items-center gap-2 relative z-10">
                        <Activity size={14} className="text-indigo-400"/> Neuro-Forecasting
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
                        {/* Amygdala: Emotion/Threat */}
                        <div className="bg-slate-900/60 p-3 rounded-lg border border-red-900/30 group relative cursor-help">
                            <div className="flex justify-between items-end mb-2">
                                <div>
                                    <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider block">Amygdala</span>
                                    <span className="text-[10px] text-slate-500">Emotional Intensity</span>
                                </div>
                                <span className="font-mono text-red-400 font-bold">
                                    {(neural.amygdala_predicted_arousal * 100).toFixed(1)}
                                </span>
                            </div>
                            <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-gradient-to-r from-red-900 to-red-500 rounded-full transition-all duration-1000"
                                    style={{ width: `${Math.min((neural.amygdala_predicted_arousal * 100), 100)}%` }}
                                />
                            </div>
                            <Tooltip text="The brain's 'smoke detector'. High activation triggers immediate attention via emotional salience (threat or excitement)." />
                        </div>

                        {/* vmPFC: Value/Reward */}
                        <div className="bg-slate-900/60 p-3 rounded-lg border border-emerald-900/30 group relative cursor-help">
                            <div className="flex justify-between items-end mb-2">
                                <div>
                                    <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider block">vmPFC</span>
                                    <span className="text-[10px] text-slate-500">Perceived Value</span>
                                </div>
                                <span className="font-mono text-emerald-400 font-bold">
                                    {(neural.vmPFC_predicted_arousal * 100).toFixed(1)}
                                </span>
                            </div>
                            <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-gradient-to-r from-emerald-900 to-emerald-500 rounded-full transition-all duration-1000"
                                    style={{ width: `${Math.min((neural.vmPFC_predicted_arousal * 100), 100)}%` }}
                                />
                            </div>
                            <Tooltip text="Processes personal relevance and value. High activation indicates the viewer feels 'this is good for me'." />
                        </div>

                        {/* dlPFC: Executive Function */}
                        <div className="bg-slate-900/60 p-3 rounded-lg border border-blue-900/30 group relative cursor-help">
                            <div className="flex justify-between items-end mb-2">
                                <div>
                                    <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider block">dlPFC</span>
                                    <span className="text-[10px] text-slate-500">Cognitive Load</span>
                                </div>
                                <span className="font-mono text-blue-400 font-bold">
                                    {(neural.dlPFC_predicted_arousal * 100).toFixed(1)}
                                </span>
                            </div>
                            <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-gradient-to-r from-blue-900 to-blue-500 rounded-full transition-all duration-1000"
                                    style={{ width: `${Math.min((neural.dlPFC_predicted_arousal * 100), 100)}%` }}
                                />
                            </div>
                            <Tooltip text="The logical center. High activation means the viewer is analyzing information rather than feeling it." />
                        </div>

                        {/* Insula/ACC: Empathy/Pain */}
                        <div className="bg-slate-900/60 p-3 rounded-lg border border-purple-900/30 group relative cursor-help">
                            <div className="flex justify-between items-end mb-2">
                                <div>
                                    <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider block">Insula / ACC</span>
                                    <span className="text-[10px] text-slate-500">Empathy & Conflict</span>
                                </div>
                                <span className="font-mono text-purple-400 font-bold">
                                    {(neural.insula_ACC_predicted_arousal * 100).toFixed(1)}
                                </span>
                            </div>
                            <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-gradient-to-r from-purple-900 to-purple-500 rounded-full transition-all duration-1000"
                                    style={{ width: `${Math.min((neural.insula_ACC_predicted_arousal * 100), 100)}%` }}
                                />
                            </div>
                            <Tooltip text="Monitors physical sensation and social pain. High activation creates a visceral, empathetic connection." />
                        </div>
                    </div>
                </div>
            )}

            {/* NEURAL SYNTHESIS TEXT */}
            {neuroNarrative && (
                <div className={`mb-6 p-4 rounded-lg border text-sm leading-relaxed ${
                    neuroNarrative.sentiment === 'positive' ? 'bg-emerald-900/10 border-emerald-500/20 text-emerald-100' :
                    neuroNarrative.sentiment === 'negative' ? 'bg-rose-900/10 border-rose-500/20 text-rose-100' :
                    neuroNarrative.sentiment === 'warning' ? 'bg-amber-900/10 border-amber-500/20 text-amber-100' :
                    'bg-indigo-900/10 border-indigo-500/20 text-indigo-100'
                }`}>
                    <div className="flex gap-3">
                        <Sparkles className={`flex-shrink-0 mt-0.5 ${
                            neuroNarrative.sentiment === 'positive' ? 'text-emerald-400' :
                            neuroNarrative.sentiment === 'negative' ? 'text-rose-400' :
                            neuroNarrative.sentiment === 'warning' ? 'text-amber-400' :
                            'text-indigo-400'
                        }`} size={18} />
                        <p dangerouslySetInnerHTML={{ __html: neuroNarrative.text.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>') }} />
                    </div>
                </div>
            )}

            {/* 2. MEMORY & COGNITION */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {memoryMetrics.memory_encoding_strength !== undefined && (
                        <div className="bg-slate-800/50 p-5 rounded-xl border border-slate-700/50 flex flex-col justify-between">
                        <h4 className="text-xs font-bold text-slate-300 uppercase tracking-widest mb-4 flex items-center gap-2">
                            <Brain size={14} className="text-cyan-400"/> Memory Encoding
                        </h4>
                        
                        <div className="flex items-end gap-3 mb-3">
                            <span className={`text-4xl font-black font-mono ${
                                memoryMetrics.memory_encoding_strength > 0.6 ? 'text-cyan-400' : 
                                memoryMetrics.memory_encoding_strength > 0.4 ? 'text-yellow-400' : 'text-slate-500'
                            }`}>
                                {(memoryMetrics.memory_encoding_strength * 100).toFixed(0)}
                            </span>
                            <span className="text-xs text-slate-400 mb-1.5 font-medium">/ 100 Strength</span>
                        </div>
                        
                        <div className="flex items-center gap-2 p-2 bg-slate-900/50 rounded-lg border border-slate-700/30">
                            <span className="text-xs text-slate-500">Recall Probability:</span>
                            <span className={`text-xs font-bold uppercase tracking-wider ${
                                memoryMetrics.recall_likelihood === 'high' ? 'text-emerald-400' :
                                memoryMetrics.recall_likelihood === 'moderate' ? 'text-yellow-400' : 'text-rose-400'
                            }`}>
                                {memoryMetrics.recall_likelihood}
                            </span>
                        </div>
                        </div>
                )}

                {/* Emotional Spectrum (Connected to Neural Data) */}
                <div className="bg-slate-800/50 p-5 rounded-xl border border-slate-700/50">
                    <h4 className="text-xs font-bold text-slate-300 uppercase tracking-widest mb-4 flex items-center gap-2">
                        <Heart size={14} className="text-rose-400"/> Emotional Spectrum
                    </h4>
                    
                    <div className="space-y-4">
                        {/* Valence Scale */}
                        <div>
                            <div className="flex justify-between text-[10px] uppercase font-bold text-slate-500 mb-1.5">
                                <span>Negative</span>
                                <span>Valence</span>
                                <span>Positive</span>
                            </div>
                            <div className="relative h-2 w-full bg-slate-700/50 rounded-full overflow-hidden">
                                <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-slate-600 z-10" />
                                <div 
                                    className={`absolute h-full ${valence > 0 ? 'bg-emerald-500' : 'bg-rose-500'}`}
                                    style={{ 
                                        left: valence < 0 ? `${50 + (valence * 50)}%` : '50%',
                                        width: `${Math.abs(valence * 50)}%` 
                                    }}
                                />
                            </div>
                            <div className="text-right mt-1">
                                <span className={`font-mono text-xs font-bold ${valence > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                    {valence > 0 ? '+' : ''}{valence.toFixed(2)}
                                </span>
                            </div>
                        </div>

                        {/* Arousal Scale */}
                        <div>
                            <div className="flex justify-between text-[10px] uppercase font-bold text-slate-500 mb-1.5">
                                <span>Calm</span>
                                <span>Arousal</span>
                                <span>Excited</span>
                            </div>
                            <div className="h-2 w-full bg-slate-700/50 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-amber-500 rounded-full transition-all duration-1000"
                                    style={{ width: `${arousal * 100}%` }}
                                />
                            </div>
                            <div className="text-right mt-1">
                                    <span className="font-mono text-xs font-bold text-amber-400">
                                    {Math.round(arousal * 100)}%
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 3. AUTHENTICITY & DIFFERENTIATION */}
            {authenticity.authenticity_score !== undefined && (
                <div className="bg-gradient-to-r from-teal-900/20 to-slate-900/50 p-5 rounded-xl border border-teal-500/20">
                    <div className="flex justify-between items-start mb-4">
                        <h4 className="text-xs font-bold text-teal-300 uppercase tracking-widest flex items-center gap-2">
                            <Fingerprint size={14} className="text-teal-400"/> Authenticity Matrix
                        </h4>
                        <div className="bg-teal-500/10 border border-teal-500/20 px-2 py-1 rounded text-xs font-bold text-teal-300">
                            {formatText(authenticity.category || 'Unknown')}
                        </div>
                    </div>

                    <div className="flex items-center gap-4 mb-4">
                        <div className="text-3xl font-black font-mono text-white">
                            {Math.round((authenticity.authenticity_score || 0) * 100)}<span className="text-lg text-slate-500">%</span>
                        </div>
                        <p className="text-sm text-slate-300 italic border-l border-teal-500/30 pl-3">
                            "{authenticity.differentiation_assessment}"
                        </p>
                    </div>

                    {authenticity.reasons?.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {authenticity.reasons.map((reason, i) => (
                                <div key={i} className="bg-slate-900/40 p-2 rounded border border-slate-700/30 flex items-start gap-2">
                                    <div className="mt-1 w-1.5 h-1.5 rounded-full bg-teal-500 flex-shrink-0" />
                                    <span className="text-xs text-slate-300">{reason}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* 4. SUBCONSCIOUS PRIMING (Striatum & Viral) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* A: Striatum / Reward System */}
                <div className="bg-slate-800/50 p-5 rounded-xl border border-slate-700/50 group relative cursor-help">
                    <h4 className="text-xs font-bold text-fuchsia-300 uppercase tracking-widest mb-4 flex items-center gap-2">
                        <Zap size={14} className="text-fuchsia-400"/> Reward Driver
                    </h4>
                    
                    <div className="flex justify-between items-end mb-2">
                        <span className="text-[10px] text-slate-500 uppercase font-bold">Striatum</span>
                        <span className="font-mono text-fuchsia-400 font-bold">{(striatum * 100).toFixed(0)}</span>
                    </div>
                    
                    <div className="h-2 w-full bg-slate-700/50 rounded-full overflow-hidden mb-3">
                        <div 
                            className="h-full bg-gradient-to-r from-fuchsia-900 to-fuchsia-500 rounded-full transition-all duration-1000"
                            style={{ width: `${striatum * 100}%` }}
                        />
                    </div>
                    
                    <p className="text-[10px] text-slate-400 leading-snug">
                        Predicts the viewer's subconscious desire to act or engage (Dopamine response).
                    </p>
                    <Tooltip text="Measures activation in the Striatum (Reward Center). High scores predict 'click' behavior and impulse actions." />
                </div>

                {/* B: Viral Psychological Triggers */}
                <div className="bg-slate-800/50 p-5 rounded-xl border border-slate-700/50">
                    <h4 className="text-xs font-bold text-indigo-300 uppercase tracking-widest mb-4 flex items-center gap-2">
                        <Layers size={14} className="text-indigo-400"/> Viral Psych Triggers
                    </h4>
                    
                    {viralFactors.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {viralFactors.map((factor, i) => (
                                <span key={i} className="px-3 py-1.5 bg-indigo-900/20 text-indigo-200 text-xs rounded-md border border-indigo-500/20 flex items-center gap-2">
                                    <Info size={12} className="text-indigo-400"/> {factor}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <div className="text-xs text-slate-500 italic">
                            No specific viral triggers detected.
                        </div>
                    )}
                </div>
            </div>

            {/* 5. SUBCONSCIOUS BRAND TRAITS */}
            {brandTraits.length > 0 && (
                <div className="bg-slate-800/50 p-5 rounded-xl border border-slate-700/50">
                    <h4 className="text-xs font-bold text-slate-300 uppercase tracking-widest mb-4 flex items-center gap-2">
                        <UserCheck size={14} className="text-emerald-400"/> Subconscious Brand Traits
                    </h4>
                    
                    <div className="flex items-start gap-4">
                        <div className="flex-1">
                            <div className="text-[10px] text-slate-500 uppercase font-bold mb-2">Detected Persona</div>
                            <div className="flex flex-wrap gap-2">
                                {brandTraits.map((trait, i) => (
                                    <div key={i} className="bg-slate-900/60 px-3 py-1.5 rounded-lg border border-slate-700/50 flex items-center gap-2">
                                        <ShieldCheck size={12} className="text-slate-400" />
                                        <span className="text-xs font-medium text-slate-200">{trait}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="w-px bg-slate-700/50 self-stretch mx-2"></div>
                        <div className="w-1/3">
                             <div className="text-[10px] text-slate-500 uppercase font-bold mb-1">Psych Vibe</div>
                             <div className="text-xs text-emerald-400 font-bold leading-relaxed">
                                {psychVibe}
                             </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};